// Transport, timecode/frame display, offsets, in/out, loop, audio and the timeline track (F-02, F-02.13).

import type { App } from '../app/app';
import type { SourceId } from '../core/types';
import { aFrameOfTick, coverage, frameLabel } from '../core/timeline';
import { displayTimecode } from '../core/timecode';
import { fmtFps } from '../core/spec-check';
import { t } from '../i18n';
import { $, esc, fmtSigned, openModal, setText } from './dom';
import { slotLabel } from '../core/types';

export function frameText(app: App, tick: number): string {
  return 'F ' + frameLabel(app.sources.timeline, tick, app.state.settings.frameNumberBase);
}

export function tcText(app: App, tick: number): string {
  const tl = app.sources.timeline;
  const a = app.sources.source('A');
  const { frame } = aFrameOfTick(tl, tick);
  const mode = app.project.timecodeMode;
  return displayTimecode(frame, tl.aFrameRate, mode, a?.spec.startTimecode);
}

let trackKey = '';

export function initTimeline(app: App): void {
  $('#timeline').addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!b) return;
    const sel = app.project.layout.selectedB;
    switch (b.dataset.act) {
      case 'start':
        app.player.seek(app.player.bounds().lo);
        break;
      case 'end':
        app.player.seek(app.player.bounds().hi);
        break;
      case 'stepBack':
        app.player.step(-1);
        break;
      case 'stepFwd':
        app.player.step(1);
        break;
      case 'play':
        app.player.toggle();
        break;
      case 'offDown':
        app.setOffset(sel, -1);
        break;
      case 'offUp':
        app.setOffset(sel, 1);
        break;
      case 'setIn':
        app.setIn();
        break;
      case 'setOut':
        app.setOut();
        break;
      case 'clearIO':
        app.clearInOut();
        break;
    }
  });
  $('#tcDisplay').addEventListener('click', () =>
    app.store.project((p) => (p.timecodeMode = p.timecodeMode === 'embedded' ? 'from-zero' : 'embedded'), { undo: false }),
  );
  $('#frameDisplay').addEventListener('click', () => openGoto());
  const loop = $<HTMLSelectElement>('#loopMode');
  loop.addEventListener('change', () => app.store.project((p) => (p.playback.loopMode = loop.value as 'loop'), { undo: false }));
  const speed = $<HTMLSelectElement>('#speedSel');
  speed.addEventListener('change', () => {
    app.store.project((p) => (p.playback.speed = Number(speed.value)), { undo: false });
    if (app.player.playing) {
      app.player.pause();
      app.player.play();
    }
  });
  const audio = $<HTMLSelectElement>('#audioSource');
  audio.addEventListener('change', () => app.setAudioSource(audio.value === 'mute' ? null : (audio.value as SourceId)));
  const vol = $<HTMLInputElement>('#audioVolume');
  vol.addEventListener('input', () => app.setVolume(Number(vol.value) / 100));
  const orig = $<HTMLInputElement>('#origOnly');
  orig.addEventListener('change', () => app.store.project((p) => (p.originalFramesOnly = orig.checked), { undo: false }));
}

function openGoto(): void {
  openModal('#gotoModal');
  const input = $<HTMLInputElement>('#gotoInput');
  input.value = '';
  setTimeout(() => input.focus(), 0);
}

export function updateTimeline(app: App): void {
  const st = app.state;
  const tl = app.sources.timeline;
  const tick = app.player.tick;
  const p = app.project;
  const hasA = app.sources.has('A');
  $('#btnPlay').textContent = st.ui.playing ? '❚❚' : '▶';
  $('#btnPlay').title = t(st.ui.playing ? 'timeline.pause' : 'timeline.play');
  const sh = st.ui.shuttle * p.playback.speed;
  setText($('#speedText'), st.ui.playing && Math.abs(sh - 1) > 1e-9 ? `${sh > 0 ? '' : '◀ '}${Math.abs(sh)}×` : '');
  setText($('#tcDisplay'), tcText(app, tick));
  const a = app.sources.source('A');
  const noEmbedded = p.timecodeMode === 'embedded' && !a?.spec.startTimecode;
  setText($('#tcMode'), noEmbedded ? t('timeline.noEmbedded') : t(p.timecodeMode === 'embedded' ? 'timeline.tcEmbedded' : 'timeline.tcZero'));
  setText($('#frameDisplay'), frameText(app, tick));
  const sel = p.layout.selectedB;
  const bs = app.sources.source(sel);
  setText($('#offsetText'), bs && app.sources.has(sel) ? t('timeline.offset', { id: sel, n: fmtSigned(bs.frameOffset) }) : t('timeline.noB'));
  $$buttons('#offsetBox button', !bs || !app.sources.has(sel));
  $<HTMLSelectElement>('#loopMode').value = p.playback.loopMode;
  $<HTMLSelectElement>('#speedSel').value = String(p.playback.speed);
  setText($('#lengthInfo'), hasA ? t('timeline.length', { n: tl.aFrameCount, fps: fmtFps(tl.aFrameRate) }) : '');
  $('#origOnlyWrap').hidden = tl.ticksPerFrame === 1;
  $<HTMLInputElement>('#origOnly').checked = p.originalFramesOnly;

  // Audio source list (F-02.11).
  const audio = $<HTMLSelectElement>('#audioSource');
  const opts = [{ v: 'mute', l: t('audio.mute') }].concat(
    app.sources.loadedIds().map((id) => {
      const rt = app.sources.runtime.get(id)!;
      return { v: id, l: rt.info.hasAudio ? slotLabel(id) : `${slotLabel(id)} ${t('audio.none')}` };
    }),
  );
  const optKey = opts.map((o) => o.v + o.l).join('|');
  if (audio.dataset.key !== optKey) {
    audio.innerHTML = opts.map((o) => `<option value="${o.v}">${esc(o.l)}</option>`).join('');
    audio.dataset.key = optKey;
  }
  audio.value = p.audioSourceId && app.sources.has(p.audioSourceId) ? p.audioSourceId : 'mute';
  $<HTMLInputElement>('#audioVolume').value = String(Math.round(p.audioVolume * 100));
  audio.title = st.ui.playing && Math.abs(sh - 1) > 1e-9 ? t('audio.onlyAt1x') : '';

  updateTrack(app);
}

function $$buttons(sel: string, disabled: boolean): void {
  document.querySelectorAll<HTMLButtonElement>(sel).forEach((b) => (b.disabled = disabled));
}

function updateTrack(app: App): void {
  const tl = app.sources.timeline;
  const p = app.project;
  const total = tl.totalTicks;
  const pct = (tick: number) => `${(tick / total) * 100}%`;
  const key = JSON.stringify([
    total,
    tl.ticksPerFrame,
    app.sources.loadedIds().map((id) => [id, app.sources.source(id)?.frameOffset, app.sources.source(id)?.frameMapping]),
    p.playback.inPoint,
    p.playback.outPoint,
  ]);
  if (key !== trackKey) {
    trackKey = key;
    // Ruler: one tick per second (or coarser for long clips).
    const secs = total / (tl.aFrameRate * tl.ticksPerFrame);
    const stepSec = secs > 120 ? 10 : secs > 40 ? 5 : 1;
    let ruler = '';
    for (let s = 0; s < secs; s += stepSec) ruler += `<div class="tick" style="left:${pct(s * tl.aFrameRate * tl.ticksPerFrame)}"><span>${s}s</span></div>`;
    $('#ruler').innerHTML = ruler;
    // Coverage per source (F-02.10).
    $('#coverage').innerHTML = app.sources
      .loadedIds()
      .map((id) => {
        const src = app.sources.tlSource(id)!;
        const c = coverage(tl, src);
        return `<div class="cov-row ${id === 'A' ? 'a' : ''}"><span class="cov-label">${slotLabel(id)}</span><div class="cov-bar" style="left:${pct(c.start)};width:${pct(Math.max(0, c.end - c.start))}"></div></div>`;
      })
      .join('');
    // Generated-frame ticks for interpolated sources (P-08).
    let gen = '';
    if (tl.ticksPerFrame > 1 && total < 4000) for (let i = 0; i < total; i++) if (i % tl.ticksPerFrame) gen += `<div class="gen-tick" style="left:${pct(i)}"></div>`;
    $('#genTicks').innerHTML = gen;
    const io = $('#ioRange');
    if (p.playback.inPoint !== undefined || p.playback.outPoint !== undefined) {
      const lo = p.playback.inPoint ?? 0;
      const hi = p.playback.outPoint ?? total - 1;
      io.style.display = 'block';
      io.style.left = pct(lo);
      io.style.width = pct(hi - lo + 1);
    } else io.style.display = 'none';
  }
  $('#playhead').style.left = pct(app.player.tick + 0.5);
  drawThumbs(app);
}

let thumbsKey = '';
/** Timeline thumbnails from the proxy scan (L0 from L1). */
function drawThumbs(app: App): void {
  const c = $<HTMLCanvasElement>('#thumbs');
  const client = app.sources.client('A');
  if (!client) {
    c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    return;
  }
  const w = c.clientWidth;
  const h = c.clientHeight;
  const key = `${w}x${h}|${client.proxyVersion}|${client.uid}`;
  if (key === thumbsKey) return;
  thumbsKey = key;
  const dpr = window.devicePixelRatio || 1;
  c.width = Math.round(w * dpr);
  c.height = Math.round(h * dpr);
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, c.width, c.height);
  const n = client.frameCount;
  if (!n) return;
  const tw = Math.round((c.height * 16) / 9);
  const count = Math.max(1, Math.floor(c.width / tw));
  for (let i = 0; i < count; i++) {
    const f = Math.min(n - 1, Math.floor(((i + 0.5) / count) * n));
    const bmp = client.getProxyBitmap(f);
    if (bmp) ctx.drawImage(bmp, i * tw, 0, tw, c.height);
  }
}
